import React from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'
import NoResults from '../../pages/shared/NoResults'
import NotesCell from './NotesCell'
import useQuery from '../../hooks/useQuery'
import QUERY_KEYS from '../../graphql/queryKeys'
import resourceImportRowsQuery from '../../graphql/queries/resourceImportRows'
import { captureErrorAndNotify } from '../../utilities/errorHandlers'
import CursorPagination from '../cursor-pagination'
import { ObjectParam, useQueryParams } from 'use-query-params'
import { Spinner } from '../../pages/shared/Spinner'

export default function ValidatedResourceImportRowsTable ({ importId, status }) {
  const [queryParams, setQueryParams] = useQueryParams({ pager: ObjectParam })

  const { data: resourceImportRowsData, isLoading } = useQuery(
    [QUERY_KEYS.resourceImportRows, {
      resourceImportId: importId,
      status,
      first: queryParams.pager?.direction === undefined || queryParams.pager?.direction === 'after' ? 10 : undefined,
      last: queryParams.pager?.direction === 'before' ? 10 : undefined,
      after: queryParams.pager?.direction === 'after' ? queryParams.pager?.cursor : undefined,
      before: queryParams.pager?.direction === 'before' ? queryParams.pager?.cursor : undefined
    }],
    resourceImportRowsQuery,
    {
      onError (error) {
        captureErrorAndNotify(error, 'Failed to retrieve data')
      },
      // We don't usually consider data to not be stale, BUT in this scenario this query gets loaded from a few components within a short time span
      // AND this data never changes. Once an import starts, its rows do not change.
      staleTime: 5 * 1000 * 60
    })

  function handlePageRequest (direction, cursor) {
    setQueryParams({ pager: { direction, cursor } })
  }

  if (isLoading) {
    return <Spinner isFetching />
  }

  return (
    <div className='table-responsive'>
      <table className='table table-striped'>
        <thead>
          <tr>
            <th width='25%'>Status</th>
            <th width='25%'>Asset ID</th>
            <th width='25%'>Placement Note</th>
            <th width='25%'>Asset Type</th>
          </tr>
        </thead>
        <tbody>
          {resourceImportRowsData.resourceImportRows.nodes.length === 0 && <tr><td colSpan='7'><NoResults /></td></tr>}
          {resourceImportRowsData.resourceImportRows.nodes.map(data => (
            <React.Fragment key={data.id}>
              <tr>
                <td className='flex justify-center'>
                  <span className={cn('flex justify-center items-center py-2 px-6 bg-opacity-90 rounded-full h-8 font-semibold', {
                    'bg-red text-white': status === 'DATA_ERRORS',
                    'bg-green': status === 'READY_FOR_IMPORT'
                  })}>
                    {status === 'READY_FOR_IMPORT' ? 'SUCCESS' : 'ERROR'}
                  </span>
                </td>
                <td>{data.uid}</td>
                <td><NotesCell notes={data.placementNote} /></td>
                <td>{data.resourceTypeName}</td>
              </tr>
              {status === 'DATA_ERRORS' && (
                <tr>
                  <td colSpan='8' style={{ backgroundColor: '#ffe3e7' }}>
                    <div className='flex justify-start items-center'>
                      <i className='material-icons text-red opacity-80 text-3xl mr-4'>info</i>
                      <span className='font-medium'>{data.dataError}</span>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      <CursorPagination
        onPageRequest={handlePageRequest}
        pageInfo={resourceImportRowsData.resourceImportRows.pageInfo}
        totalCount={resourceImportRowsData.resourceImportRows.totalCount}
      />
    </div>
  )
}

ValidatedResourceImportRowsTable.propTypes = {
  importId: PropTypes.string.isRequired,
  status: PropTypes.oneOf(['READY_FOR_IMPORT', 'DATA_ERRORS']).isRequired
}
